/**
 * ReactMessageView — Zero push-based View for React.
 *
 * Implements Zero's Output interface to receive individual add/edit/remove
 * push events instead of diffing full arrays. Per-ID subscriptions via
 * useSyncExternalStore — each MessageRow re-renders only when its specific
 * message changes.
 *
 * First React implementation of Zero's push-based View API.
 * Reference: zero-solid's SolidView (same pattern, different reactivity bridge).
 */
import { useLayoutEffect } from "react";
import { useZero } from "@rocicorp/zero/react";
import { RENDER_DEBUG } from "./debug-flags";
import { zql } from "@ano/shared/schema";
import type { MessageData } from "./message-data-store";
import type { Change, Output, Input, InputBase } from "./zero-ivm-types";
import { materializeChange, skipYields } from "./zero-ivm-types";

// ─── Shared interface ────────────────────────────────────────────────

/** Common API for MessageDataStore and ReactMessageView. */
export interface MessageEntrySource {
  get(id: string): MessageData | undefined;
  subscribe(id: string, cb: () => void): () => void;
}

// ─── ReactMessageView ────────────────────────────────────────────────

const DESTROY_DELAY_MS = 5000; // matches existing ViewCache TTL

export class ReactMessageView implements MessageEntrySource {
  #entries = new Map<string, MessageData>();
  #idListeners = new Map<string, Set<() => void>>();
  #pendingIds = new Set<string>();
  #dirty = false;
  #destroyed = false;
  #channelIdHint: string | null = null;

  constructor(
    input: Input,
    onTransactionCommit: (cb: () => void) => void,
    channelIdHint?: string,
  ) {
    this.#channelIdHint = channelIdHint ?? null;

    // Register to receive push events
    input.setOutput(this as unknown as Output);

    // Hydrate initial data from IVM (synchronous)
    for (const node of skipYields(input.fetch({}))) {
      const row = node.row as unknown as MessageData;
      this.#entries.set(row.id, row);
    }

    if (RENDER_DEBUG)
      console.debug(
        `[ReactMessageView] HYDRATE ${this.#entries.size} entries channel=${this.#channelIdHint ?? "?"}`,
      );

    // Notify at Zero's transaction boundary (outside React render)
    onTransactionCommit(() => this.#flush());
  }

  /** Zero calls this with individual change events. */

  push(rawChange: Change, _pusher: InputBase): Iterable<"yield"> {
    if (this.#destroyed) return [] as unknown as Iterable<"yield">;

    // Zero's IVM pushes tuple-shaped changes ([typeIdx, node, extra]).
    // Both reference Views (zero-solid's `materializeRelationships`
    // and ArrayView's `changeToViewChange`) convert to {type, node, ...}
    // before dispatch. Without this, our switch on `change.type`
    // defaults — tuples have no `.type` property — and every change
    // is dropped silently, producing the empty-channel render bug.
    const change = materializeChange(rawChange as unknown);
    if (!change) {
      console.warn(
        `[ReactMessageView] could not materialize change channel=${this.#channelIdHint ?? "?"}`,
        rawChange,
      );
      return [] as unknown as Iterable<"yield">;
    }

    switch (change.type) {
      case "add":
      case "edit": {
        const row = change.node.row as unknown as MessageData;
        this.#entries.set(row.id, row);
        this.#pendingIds.add(row.id);
        this.#dirty = true;
        if (RENDER_DEBUG)
          console.debug(
            `[ReactMessageView] ${change.type.toUpperCase()} msg=${row.id.slice(-6)}`,
          );
        break;
      }
      case "remove": {
        const row = change.node.row as unknown as MessageData;
        this.#entries.delete(row.id);
        this.#pendingIds.add(row.id);
        this.#dirty = true;
        if (RENDER_DEBUG)
          console.debug(`[ReactMessageView] REMOVE msg=${row.id.slice(-6)}`);
        break;
      }
      case "child":
        // Query has no .related(), so no child events expected.
        if (RENDER_DEBUG) console.debug("[ReactMessageView] CHILD ignored");
        break;
    }
    return [] as unknown as Iterable<"yield">;
  }

  #flush() {
    if (this.#destroyed || !this.#dirty) return;
    this.#dirty = false;
    if (RENDER_DEBUG)
      console.debug(
        `[ReactMessageView] FLUSH ${this.#pendingIds.size} notifications`,
      );
    for (const id of this.#pendingIds) {
      const listeners = this.#idListeners.get(id);
      if (listeners) for (const cb of listeners) cb();
    }
    this.#pendingIds.clear();
  }

  get(id: string): MessageData | undefined {
    return this.#entries.get(id);
  }

  subscribe(id: string, cb: () => void): () => void {
    if (this.#destroyed) return () => {};
    let set = this.#idListeners.get(id);
    if (!set) {
      set = new Set();
      this.#idListeners.set(id, set);
    }
    set.add(cb);
    return () => {
      set!.delete(cb);
      if (set!.size === 0) this.#idListeners.delete(id);
    };
  }

  destroy() {
    this.#destroyed = true;
    this.#idListeners.clear();
    this.#pendingIds.clear();
    this.#entries.clear();
  }

  get size(): number {
    return this.#entries.size;
  }
}

// ─── Factory ─────────────────────────────────────────────────────────

function createReactMessageViewFactory(channelIdHint: string) {
  return (
    _query: unknown,
    input: Input,
    _format: unknown,
    onDestroy: () => void,
    onTransactionCommit: (cb: () => void) => void,

    _queryComplete: unknown,

    _updateTTL: unknown,
  ) => {
    const view = new ReactMessageView(
      input,
      onTransactionCommit,
      channelIdHint,
    );
    return {
      view,
      destroy: () => {
        view.destroy();
        onDestroy();
      },
    };
  };
}

// ─── Store (module-level render-safe cache) ──────────────────────────

type MessageViewHandle = Readonly<{
  view: ReactMessageView;
  key: string;
  acquire: () => void;
  release: () => void;
}>;

type CacheEntry = {
  view: ReactMessageView;
  handle: MessageViewHandle;
  destroyFn: () => void;
  refCount: number;
  destroyTimer: ReturnType<typeof setTimeout> | null;
};

class ReactMessageViewStore {
  #entries = new Map<string, CacheEntry>();

  /**
   * Called during render — intentional render-time materialization
   * (matches Zero's ViewStore pattern). Synchronous, idempotent.
   * Same key returns same frozen handle reference.
   *
   * Does NOT cancel existing destroy timers — abandoned renders
   * must not keep zero-ref entries alive.
   */
  getOrCreate(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    zero: any,
    cacheKey: string,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    query: any,
    channelIdHint: string,
    ttl?: string,
  ): MessageViewHandle {
    let entry = this.#entries.get(cacheKey);
    if (entry) {
      return entry.handle;
    }

    // Materialize — intentional render-time side effect
    const factory = createReactMessageViewFactory(channelIdHint);
    const result = zero.materialize(query, factory, ttl ? { ttl } : undefined);
    entry = {
      view: result.view,
      destroyFn: result.destroy,
      refCount: 0,
      destroyTimer: null,
      handle: null!,
    };
    entry.handle = Object.freeze({
      view: entry.view,
      key: cacheKey,
      acquire: () => this.#acquire(cacheKey),
      release: () => this.#release(cacheKey),
    });
    this.#entries.set(cacheKey, entry);

    // Arm timer immediately — if acquire() is never called
    // (abandoned render), this ensures cleanup.
    entry.destroyTimer = setTimeout(
      () => this.#destroyEntry(cacheKey),
      DESTROY_DELAY_MS,
    );

    return entry.handle;
  }

  /** Called from useLayoutEffect — committed render only. */
  #acquire(key: string) {
    const e = this.#entries.get(key);
    if (!e) return;
    if (e.destroyTimer) {
      clearTimeout(e.destroyTimer);
      e.destroyTimer = null;
    }
    e.refCount++;
  }

  /** Called from useLayoutEffect cleanup. */
  #release(key: string) {
    const e = this.#entries.get(key);
    if (!e) return;
    e.refCount--;
    if (e.refCount <= 0 && !e.destroyTimer) {
      e.destroyTimer = setTimeout(
        () => this.#destroyEntry(key),
        DESTROY_DELAY_MS,
      );
    }
  }

  #destroyEntry(key: string) {
    const entry = this.#entries.get(key);
    if (!entry || entry.refCount > 0) return;
    entry.destroyFn(); // single path: view.destroy() + onDestroy()
    this.#entries.delete(key);
  }
}

const reactMessageViewStore = new ReactMessageViewStore();

// ─── Hook ────────────────────────────────────────────────────────────

/**
 * Render-safe hook for push-based per-ID message subscriptions.
 * View available synchronously on first render. useLayoutEffect
 * for acquire/release eliminates timer race.
 */
export function useReactMessageView(
  channelId: string,
  messageLimit: number,
): ReactMessageView {
  const zero = useZero();
  // Raw ZQL — no whereExists. Permissions enforced by MessageArea's
  // defineQuery sync path. This materialization reads local data only,
  // so the IVM tracks only the messages table. Zero CHILD events.
  const query = zql.messages
    .where("channel_id", channelId)
    .where("thread_id", "IS", null)
    .orderBy("created_at", "desc")
    .orderBy("id", "desc")
    .limit(messageLimit);
  const cacheKey = `messages:${channelId}:${messageLimit}`;

  // Render-time: memoized handle (same ref for same key)
  const handle = reactMessageViewStore.getOrCreate(
    zero,
    cacheKey,
    query,
    channelId,
    "5m",
  );

  // useLayoutEffect — fires synchronously after commit, before paint.
  // Eliminates timer race: acquire() cancels destroy timer before it fires.
  useLayoutEffect(() => {
    handle.acquire();
    return () => handle.release();
  }, [handle]);

  return handle.view;
}
